<template>
  <div class="w-full p-5 md:w-128 pt-16 sm:pt-5" data-testid="custom-thumbnail-modal">
    <h5 class="modal-title">Choose Thumbnail</h5>

    <Tabs class="mt-4" :selected-tab="activeTab" @tab-click="onTabClick">
      <div class="mt-4">
        <Tab id="upload-image" name="Upload Image">
          <div class="upload-tab" data-testid="custom-thumbnail-upload-tab">
            <div
              class="drop-zone"
              :class="{ 'drop-zone-active': isDragging }"
              data-testid="custom-thumbnail-drop-zone"
              @click="triggerFileInput"
              @dragover.prevent="isDragging = true"
              @dragleave.prevent="isDragging = false"
              @drop.prevent="onDrop"
            >
              <img v-if="previewUrl" :src="previewUrl" class="preview-image" data-testid="custom-thumbnail-preview" />
              <template v-else>
                <Icon name="general.uploadCloud" size="lg" class="drop-zone-icon" />
                <p class="drop-zone-text">Drag and drop an image, or click to browse</p>
              </template>

              <input
                ref="fileInput"
                type="file"
                accept="image/png,image/jpeg,image/gif"
                class="hidden-input"
                data-testid="custom-thumbnail-file-input"
                @change="onFileChange"
              />
            </div>
            <p class="upload-hint">JPEG, PNG, or GIF · up to 2MB · recommended 1920×1080px</p>
            <p v-if="selectionError" class="upload-hint-error" data-testid="custom-thumbnail-file-error">{{ selectionError }}</p>
          </div>
        </Tab>
        <Tab id="choose-frame" name="Choose Frame" :hidden="!isVideo">
          <ChooseFrameTab
            v-if="id && contentUrl"
            :item-id="id"
            :content-url="contentUrl"
            :current-timestamp="currentThumbnailTimestamp"
            data-testid="custom-thumbnail-choose-frame-tab"
            @select="onSelectFrame"
          />
        </Tab>
      </div>
    </Tabs>

    <div class="modal-footer">
      <Button type="link" variant="black" data-testid="custom-thumbnail-cancel" @click="onCancel">Cancel</Button>
      <Button
        type="solid"
        variant="blue"
        :disabled="!hasSelection || isUploading"
        :loading="isUploading"
        data-testid="custom-thumbnail-upload"
        @click="onUpload"
      >
        Upload
      </Button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onUnmounted, ref } from 'vue';
import { storeToRefs } from 'pinia';
import { Tabs, Tab } from '@/storybook/tabs';
import { Button, Icon } from '@/storybook';
import { ItemType, useViewerItemStore } from '@/modules/viewer/store/useViewerItemStore';
import { useToast } from '@/composables/useToast';
import ChooseFrameTab from './ChooseFrameTab.vue';

const emit = defineEmits<{ close: [] }>();

const viewerItemStore = useViewerItemStore();
const { id, item } = storeToRefs(viewerItemStore);
const { success, error } = useToast();

type ThumbnailTab = '#upload-image' | '#choose-frame';

const activeTab = ref<ThumbnailTab>('#upload-image');

const onTabClick = (tab: string) => {
  activeTab.value = tab as ThumbnailTab;
};

const contentUrl = computed(() => item.value?.links?.contentUrl ?? '');

const isVideo = computed(() => item.value?.attributes?.itemType === ItemType.VIDEO);

const currentThumbnailTimestamp = computed(() => item.value?.attributes?.thumbnailTimestamp ?? null);

// Mirrors app/controllers/api/v5/items/thumbnail_controller.rb's
// thumbnail_file_error — validating here lets the user see why a file was
// rejected immediately, instead of only after a round-trip on Upload.
// There's deliberately no pixel-dimension cap: 1920x1080 is the recommended
// target, but a higher-resolution export of that same design (2x, 4x, ...)
// is legitimate — file size is what actually bounds the upload.
const MAX_FILE_SIZE_BYTES = 2 * 1024 * 1024;
const ALLOWED_MIME_TYPES = ['image/jpeg', 'image/png', 'image/gif'];

const fileInput = ref<HTMLInputElement | null>(null);
const selectedFile = ref<File | null>(null);
const previewUrl = ref<string | null>(null);
const selectionError = ref<string | null>(null);
const isDragging = ref(false);
const isUploading = ref(false);
const selectedTimestamp = ref<number | null>(null);

const hasSelection = computed(() => (activeTab.value === '#upload-image' ? !!selectedFile.value : selectedTimestamp.value != null));

const triggerFileInput = () => fileInput.value?.click();

// Confirms the browser can actually decode the file as an image — a renamed
// non-image file can pass the MIME-type check above but fail to load here.
const verifyImageLoads = (objectUrl: string): Promise<void> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve();
    image.onerror = () => reject(new Error('Invalid image file'));
    image.src = objectUrl;
  });

const setSelectedFile = async (file?: File | null) => {
  if (!file) {
    return;
  }

  if (previewUrl.value) {
    URL.revokeObjectURL(previewUrl.value);
    previewUrl.value = null;
  }
  selectedFile.value = null;
  selectionError.value = null;

  if (file.size > MAX_FILE_SIZE_BYTES) {
    selectionError.value = `File must be less than ${MAX_FILE_SIZE_BYTES / (1024 * 1024)}MB`;
    return;
  }
  if (!ALLOWED_MIME_TYPES.includes(file.type)) {
    selectionError.value = 'File must be a JPEG, PNG, or GIF image';
    return;
  }

  const objectUrl = URL.createObjectURL(file);
  try {
    await verifyImageLoads(objectUrl);
  } catch {
    selectionError.value = 'Invalid image file';
    URL.revokeObjectURL(objectUrl);
    return;
  }

  selectedFile.value = file;
  previewUrl.value = objectUrl;
};

onUnmounted(() => {
  if (previewUrl.value) {
    URL.revokeObjectURL(previewUrl.value);
  }
});

const onFileChange = async (event: Event) => {
  const input = event.target as HTMLInputElement;
  await setSelectedFile(input.files?.[0]);
};

const onDrop = async (event: DragEvent) => {
  isDragging.value = false;
  await setSelectedFile(event.dataTransfer?.files?.[0]);
};

const onSelectFrame = (timestamp: number) => {
  selectedTimestamp.value = timestamp;
};

const onCancel = () => emit('close');

const applyThumbnail = () => {
  success('Thumbnail updated', { icon: 'general.checkedCircleSolid' });
  emit('close');
};

const onUpload = async () => {
  if (!hasSelection.value || isUploading.value) {
    return;
  }

  isUploading.value = true;
  try {
    if (activeTab.value === '#upload-image' && selectedFile.value) {
      await viewerItemStore.uploadCustomThumbnail({ file: selectedFile.value });
    } else if (activeTab.value === '#choose-frame' && selectedTimestamp.value != null) {
      await viewerItemStore.updateThumbnailTimestamp(selectedTimestamp.value);
    }
    applyThumbnail();
  } catch (e: any) {
    error(e?.errors?.[0]?.detail || 'Failed to update thumbnail');
  } finally {
    isUploading.value = false;
  }
};
</script>

<style scoped>
.modal-title {
  @apply m-0 text-lg font-semibold text-zinc-900 dark:text-zinc-100;
}

.upload-tab {
  @apply flex flex-col gap-4;
}

.drop-zone {
  @apply relative flex flex-col items-center justify-center gap-2 h-52
         border-2 border-dashed border-zinc-300 dark:border-zinc-600
         rounded-lg cursor-pointer text-center px-4
         hover:border-blue-400 dark:hover:border-blue-500
         transition-colors;
}

.drop-zone-active {
  @apply border-blue-500 bg-blue-50 dark:bg-blue-900/20;
}

.drop-zone-icon {
  @apply text-zinc-400 dark:text-zinc-500;
}

.drop-zone-text {
  @apply text-sm text-zinc-500 dark:text-zinc-400;
}

.upload-hint-error {
  @apply text-xs text-red-500 dark:text-red-400 text-center;
}

.upload-hint {
  @apply text-xs text-zinc-400 dark:text-zinc-500 text-center;
}

.preview-image {
  @apply w-full h-full object-cover rounded-md;
}

.hidden-input {
  @apply hidden;
}

.modal-footer {
  @apply flex justify-end gap-2 mt-6;
}

:deep(.tabs-component-tabs) {
  /* .tabs-component is flex-col, whose default align-items: stretch forces
     any direct flex child to full width — self-start opts this one out so
     inline-flex actually hugs its content instead. */
  @apply inline-flex self-start items-center gap-0 p-1 rounded-lg bg-zinc-100 dark:bg-zinc-800;
}

:deep(.tabs-component-tab-a) {
  @apply block w-full text-center rounded-md py-1.5 px-2 text-sm font-medium
         text-zinc-500 dark:text-zinc-400;
}

:deep(.tabs-component-tab-a.is-active) {
  @apply bg-white dark:bg-zinc-700 text-zinc-900 dark:text-zinc-100 shadow-sm;
}

:deep(.tabs-component-tab-a.is-active::after) {
  content: none;
}
</style>
